import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRequests } from '../hooks/useRequests';
import { ArrowLeft, UserPlus, Home as HomeIcon } from 'lucide-react';
import type { RequestFormData, NewCustomerData, NewPropertyData } from '../types';
import { useCustomers } from '../../customers/hooks/useCustomers';
import { useServices } from '../../services/hooks/useServices';
import { useProperties } from '../../properties/hooks/useProperties';
import { supabase } from '@cleanpro/common-ui';

export default function CreateRequest() {
  const { addRequest } = useRequests();
  const { customers, createCustomer } = useCustomers();
  const { services } = useServices();
  const { properties, addProperty } = useProperties();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<RequestFormData>({
    customerId: '',
    serviceId: '',
    propertyId: null,
    requestedTime: '',
    notes: '',
    urgency: 'normal',
    estimatedDuration: undefined,
    accessInstructions: ''
  });

  // Separate state for date and time to make UI clearer
  const [requestedDate, setPreferredDate] = useState('');
  const [requestedTime, setPreferredTime] = useState('');

  // New customer form data
  const [newCustomer, setNewCustomer] = useState<NewCustomerData>({
    name: '',
    email: '',
    phone: '',
    address: ''
  });

  // New property form data
  const [newProperty, setNewProperty] = useState<NewPropertyData>({
    name: '',
    propertyType: 'house',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    squareFootage: undefined,
    bedrooms: undefined,
    bathrooms: undefined,
    floors: undefined,
    accessInstructions: '',
    specialInstructions: '',
    hasPets: false,
    petDetails: ''
  });

  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [isNewProperty, setIsNewProperty] = useState(false);

  // Filter customers based on search (name, email, or phone)
  const filteredCustomers = customers.filter((customer) =>
    (customer.name ?? '').toLowerCase().includes(customerSearch.toLowerCase()) ||
    (customer.email ?? '').toLowerCase().includes(customerSearch.toLowerCase()) ||
    (customer.phone ?? '').toLowerCase().includes(customerSearch.toLowerCase())
  );

  const selectedCustomer = customers.find(c => c.id === formData.customerId);
  const selectedService = services.find(s => s.id === formData.serviceId);

  // Filter properties for the selected customer
  const customerProperties = properties.filter(p => p.customerId === formData.customerId);

  const handleCustomerSelect = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customerId: customer.id ?? '',
        propertyId: null // Reset property when customer changes
      }));
      setCustomerSearch(customer.name ?? '');
      setShowCustomerDropdown(false);
      setIsNewCustomer(false);
    }
  };

  const handleNewCustomerToggle = () => {
    setIsNewCustomer(!isNewCustomer);
    setFormData(prev => ({ ...prev, customerId: '', propertyId: null }));
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  const handleNewPropertyToggle = () => {
    setIsNewProperty(!isNewProperty);
    setFormData(prev => ({ ...prev, propertyId: null }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (isNewCustomer) {
      if (!newCustomer.name || !newCustomer.email || !newCustomer.phone || !newCustomer.address) {
        alert('Please fill in all required customer fields');
        return;
      }
    } else if (!formData.customerId) {
      alert('Please select a customer');
      return;
    }

    if (!formData.serviceId) {
      alert('Please select a service');
      return;
    }

    if (isNewProperty) {
      if (!newProperty.name || !newProperty.addressLine1 || !newProperty.city || 
          !newProperty.state || !newProperty.postalCode) {
        alert('Please fill in all required property fields');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      let finalCustomerId = formData.customerId;
      let finalPropertyId = formData.propertyId;

      // Step 1: Create customer if needed
      if (isNewCustomer) {
        try {
          console.log('Creating new customer:', newCustomer);
          await createCustomer(newCustomer);
          
          // Query the database directly to get the newly created customer
          // This avoids issues with React state updates in async functions
          const { data: freshCustomers, error: fetchError } = await supabase
            .from('customers')
            .select('id, email')
            .eq('email', newCustomer.email)
            .limit(1);
          
          if (fetchError) {
            console.error('Error fetching created customer:', fetchError);
            throw new Error('Customer created but could not be retrieved. Please try again.');
          }
          
          if (freshCustomers && freshCustomers.length > 0 && freshCustomers[0].id) {
            console.log('Found customer with ID:', freshCustomers[0].id);
            finalCustomerId = freshCustomers[0].id;
          } else {
            console.error('Customer not found after creation');
            throw new Error('Customer created but could not be found. Please try again.');
          }
        } catch (err) {
          console.error('Error in customer creation process:', err);
          throw err;
        }
      }

      // Validate we have a customer ID before proceeding
      if (!finalCustomerId) {
        throw new Error('Customer ID is required to create a request');
      }

      // Step 2: Create property if needed
      if (isNewProperty && finalCustomerId) {
        await addProperty({
          ...newProperty,
          customerId: finalCustomerId,
          bedrooms: newProperty.bedrooms || 0,
          bathrooms: newProperty.bathrooms || 0,
          floors: newProperty.floors || 1,
          hasPets: newProperty.hasPets || false,
          isActive: true
        });
        
        // Find the newly created property
        await new Promise(resolve => setTimeout(resolve, 500));
        const customerProps = properties.filter(p => p.customerId === finalCustomerId);
        const createdProperty = customerProps.find(p => 
          p.addressLine1 === newProperty.addressLine1 && 
          p.postalCode === newProperty.postalCode
        );
        if (createdProperty?.id) {
          finalPropertyId = createdProperty.id;
        }
      }

      // Step 3: Create the request
      // Combine date and time, ensuring we never send empty strings
      let combinedDateTime = null;
      if (requestedDate?.trim()) {
        if (requestedTime?.trim()) {
          combinedDateTime = `${requestedDate}T${requestedTime}`;
        } else {
          combinedDateTime = requestedDate;
        }
      }

      await addRequest({
        customerId: finalCustomerId,
        serviceId: formData.serviceId,
        propertyId: finalPropertyId ?? null,
        requestedDateTime: combinedDateTime,
        notes: formData.notes?.trim() ? formData.notes.trim() : null,
        status: 'pending',
        urgency: formData.urgency,
        estimatedDuration: formData.estimatedDuration ?? null,
        accessInstructions: formData.accessInstructions ?? null,
        referral: null
      });
      
      navigate('/requests');
    } catch (error) {
      console.error('Error creating request:', error);
      alert('Failed to create request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => navigate('/requests')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Create Request</h1>
          <p className="text-gray-600 mt-1">Create a new customer service request</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Customer Selection or Creation */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Step 1: Customer Information</h2>
            <button
              type="button"
              onClick={handleNewCustomerToggle}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
            >
              <UserPlus className="h-4 w-4" />
              {isNewCustomer ? 'Select Existing' : 'Create New Customer'}
            </button>
          </div>
          
          {!isNewCustomer ? (
            <div className="space-y-4">
              <div>
                <label htmlFor="customer-search" className="block text-sm font-medium text-gray-700 mb-2">
                  Search Customer (by name, email, or phone)
                </label>
                <div className="relative">
                  <input
                    id="customer-search"
                    type="text"
                    placeholder="Start typing to search..."
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  
                  {showCustomerDropdown && filteredCustomers.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {filteredCustomers.map(customer => (
                        <button
                          key={customer.id}
                          type="button"
                          onClick={() => customer.id && handleCustomerSelect(customer.id)}
                          className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{customer.name}</div>
                          <div className="text-sm text-gray-600">{customer.email}</div>
                          <div className="text-sm text-gray-500">{customer.phone}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {showCustomerDropdown && customerSearch && filteredCustomers.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 text-center text-gray-500">
                      No customers found. Click "Create New Customer" to add one.
                    </div>
                  )}
                </div>
                
                {selectedCustomer && (
                  <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="font-medium text-blue-900">{selectedCustomer.name}</div>
                    <div className="text-sm text-blue-700">{selectedCustomer.email}</div>
                    <div className="text-sm text-blue-600">{selectedCustomer.phone}</div>
                    <div className="text-sm text-blue-600">{selectedCustomer.address}</div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="new-customer-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    id="new-customer-name"
                    type="text"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="new-customer-email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    id="new-customer-email"
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="john@example.com"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="new-customer-phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone *
                  </label>
                  <input
                    id="new-customer-phone"
                    type="tel"
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="(555) 123-4567"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="new-customer-address" className="block text-sm font-medium text-gray-700 mb-2">
                    Mailing Address *
                  </label>
                  <input
                    id="new-customer-address"
                    type="text"
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="123 Main St, City, ST 12345"
                    required
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Step 2: Property Selection or Creation */}
        {(selectedCustomer || isNewCustomer) && (
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Step 2: Property Information</h2>
              {selectedCustomer && (
                <button
                  type="button"
                  onClick={handleNewPropertyToggle}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  <HomeIcon className="h-4 w-4" />
                  {isNewProperty ? 'Select Existing' : 'Create New Property'}
                </button>
              )}
            </div>
            
            {!isNewProperty && selectedCustomer ? (
              <div className="space-y-4">
                <div>
                  <label htmlFor="property-select" className="block text-sm font-medium text-gray-700 mb-2">
                    Select Property
                  </label>
                  <select
                    id="property-select"
                    value={formData.propertyId ?? ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, propertyId: e.target.value || null }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a property (optional)</option>
                    {customerProperties.map(property => (
                      <option key={property.id} value={property.id}>
                        {property.name} - {property.addressLine1}, {property.city}, {property.state}
                      </option>
                    ))}
                  </select>
                  {customerProperties.length === 0 && (
                    <p className="mt-2 text-sm text-gray-500">
                      No properties found for this customer. Click "Create New Property" to add one.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="new-property-name" className="block text-sm font-medium text-gray-700 mb-2">
                      Property Name *
                    </label>
                    <input
                      id="new-property-name"
                      type="text"
                      value={newProperty.name}
                      onChange={(e) => setNewProperty(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Main Residence"
                      required={isNewProperty}
                    />
                  </div>
                  <div>
                    <label htmlFor="new-property-type" className="block text-sm font-medium text-gray-700 mb-2">
                      Property Type *
                    </label>
                    <select
                      id="new-property-type"
                      value={newProperty.propertyType}
                      onChange={(e) => setNewProperty(prev => ({ 
                        ...prev, 
                        propertyType: e.target.value as NewPropertyData['propertyType']
                      }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required={isNewProperty}
                    >
                      <option value="house">House</option>
                      <option value="apartment">Apartment</option>
                      <option value="condo">Condo</option>
                      <option value="townhouse">Townhouse</option>
                      <option value="office">Office</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="new-property-address1" className="block text-sm font-medium text-gray-700 mb-2">
                    Address Line 1 *
                  </label>
                  <input
                    id="new-property-address1"
                    type="text"
                    value={newProperty.addressLine1}
                    onChange={(e) => setNewProperty(prev => ({ ...prev, addressLine1: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="123 Main Street"
                    required={isNewProperty}
                  />
                </div>
                
                <div>
                  <label htmlFor="new-property-address2" className="block text-sm font-medium text-gray-700 mb-2">
                    Address Line 2
                  </label>
                  <input
                    id="new-property-address2"
                    type="text"
                    value={newProperty.addressLine2}
                    onChange={(e) => setNewProperty(prev => ({ ...prev, addressLine2: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Apt 4B"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="new-property-city" className="block text-sm font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      id="new-property-city"
                      type="text"
                      value={newProperty.city}
                      onChange={(e) => setNewProperty(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="City"
                      required={isNewProperty}
                    />
                  </div>
                  <div>
                    <label htmlFor="new-property-state" className="block text-sm font-medium text-gray-700 mb-2">
                      State *
                    </label>
                    <input
                      id="new-property-state"
                      type="text"
                      value={newProperty.state}
                      onChange={(e) => setNewProperty(prev => ({ ...prev, state: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="ST"
                      maxLength={2}
                      required={isNewProperty}
                    />
                  </div>
                  <div>
                    <label htmlFor="new-property-postal" className="block text-sm font-medium text-gray-700 mb-2">
                      Postal Code *
                    </label>
                    <input
                      id="new-property-postal"
                      type="text"
                      value={newProperty.postalCode}
                      onChange={(e) => setNewProperty(prev => ({ ...prev, postalCode: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="12345"
                      required={isNewProperty}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label htmlFor="new-property-bedrooms" className="block text-sm font-medium text-gray-700 mb-2">
                      Bedrooms
                    </label>
                    <input
                      id="new-property-bedrooms"
                      type="number"
                      min="0"
                      value={newProperty.bedrooms ?? ''}
                      onChange={(e) => setNewProperty(prev => ({ 
                        ...prev, 
                        bedrooms: e.target.value ? parseInt(e.target.value) : undefined 
                      }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="new-property-bathrooms" className="block text-sm font-medium text-gray-700 mb-2">
                      Bathrooms
                    </label>
                    <input
                      id="new-property-bathrooms"
                      type="number"
                      min="0"
                      step="0.5"
                      value={newProperty.bathrooms ?? ''}
                      onChange={(e) => setNewProperty(prev => ({ 
                        ...prev, 
                        bathrooms: e.target.value ? parseFloat(e.target.value) : undefined 
                      }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="new-property-floors" className="block text-sm font-medium text-gray-700 mb-2">
                      Floors
                    </label>
                    <input
                      id="new-property-floors"
                      type="number"
                      min="1"
                      value={newProperty.floors ?? ''}
                      onChange={(e) => setNewProperty(prev => ({ 
                        ...prev, 
                        floors: e.target.value ? parseInt(e.target.value) : undefined 
                      }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="new-property-sqft" className="block text-sm font-medium text-gray-700 mb-2">
                      Sq. Ft.
                    </label>
                    <input
                      id="new-property-sqft"
                      type="number"
                      min="0"
                      value={newProperty.squareFootage ?? ''}
                      onChange={(e) => setNewProperty(prev => ({ 
                        ...prev, 
                        squareFootage: e.target.value ? parseInt(e.target.value) : undefined 
                      }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    id="new-property-pets"
                    type="checkbox"
                    checked={newProperty.hasPets}
                    onChange={(e) => setNewProperty(prev => ({ ...prev, hasPets: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="new-property-pets" className="text-sm font-medium text-gray-700">
                    Property has pets
                  </label>
                </div>
                
                {newProperty.hasPets && (
                  <div>
                    <label htmlFor="new-property-pet-details" className="block text-sm font-medium text-gray-700 mb-2">
                      Pet Details
                    </label>
                    <textarea
                      id="new-property-pet-details"
                      value={newProperty.petDetails}
                      onChange={(e) => setNewProperty(prev => ({ ...prev, petDetails: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={2}
                      placeholder="e.g., 2 dogs, friendly"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Service Information */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Step 3: Service Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="service-select" className="block text-sm font-medium text-gray-700 mb-2">
                Service *
              </label>
              <select
                id="service-select"
                value={formData.serviceId}
                onChange={(e) => setFormData(prev => ({ ...prev, serviceId: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select a service</option>
                {services.map(service => (
                  <option key={service.id} value={service.id}>
                    {service.name} - ${service.basePrice}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="urgency-select" className="block text-sm font-medium text-gray-700 mb-2">
                Urgency
              </label>
              <select
                id="urgency-select"
                value={formData.urgency}
                onChange={(e) => setFormData(prev => ({ ...prev, urgency: e.target.value as RequestFormData['urgency'] }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>

            <div>
              <label htmlFor="preferred-date" className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Date
              </label>
              <input
                id="preferred-date"
                type="date"
                value={requestedDate}
                onChange={(e) => setPreferredDate(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="preferred-time" className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Time
              </label>
              <input
                id="preferred-time"
                type="time"
                value={requestedTime}
                onChange={(e) => setPreferredTime(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label htmlFor="estimated-duration" className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Duration (hours)
              </label>
              <input
                id="estimated-duration"
                type="number"
                step="0.5"
                min="0.5"
                value={formData.estimatedDuration ?? ''}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  estimatedDuration: e.target.value ? parseFloat(e.target.value) : undefined 
                }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. 2.5"
              />
            </div>
          </div>

          {selectedService && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="font-medium text-green-900">{selectedService.name}</div>
              <div className="text-sm text-green-700">{selectedService.description}</div>
              <div className="text-sm text-green-600">Base Price: ${selectedService.basePrice}</div>
              <div className="text-sm text-green-600">Default Duration: {selectedService.duration} hours</div>
            </div>
          )}
        </div>

        {/* Step 4: Additional Information */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Step 4: Additional Details</h2>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="access-instructions" className="block text-sm font-medium text-gray-700 mb-2">
                Access Instructions
              </label>
              <textarea
                id="access-instructions"
                value={formData.accessInstructions}
                onChange={(e) => setFormData(prev => ({ ...prev, accessInstructions: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={2}
                placeholder="Gate codes, parking instructions, keys location, etc."
              />
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Notes & Special Requirements
              </label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder="Any special requirements, concerns, or additional details"
              />
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/requests')}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Creating...' : 'Create Request'}
          </button>
        </div>
      </form>
    </div>
  );
}